import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import type { Props as ButtonProps } from '@sorare/core/src/atoms/buttons/Button';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ANY_SPORT_SCOUTING_CENTER_WATCHLISTS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';

import { DestroyWatchlistDialog } from '../DestroyWatchlistDialog';
import type { DestroyWatchlistButton_watchlist } from './__generated__/index.graphql';

type Props = {
  watchlist: DestroyWatchlistButton_watchlist;
  onClose?: () => void;
} & Pick<ButtonProps, 'color' | 'size' | 'blur'>;

export const DestroyWatchlistButton = ({
  watchlist,
  onClose,
  ...props
}: Props) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { generateSportContextPath } = useSportContext();

  return (
    <>
      <Button
        {...props}
        startIcon={<FontAwesomeIcon icon={faTrash} size="sm" />}
        onClick={() => setOpen(true)}
      >
        <FormattedMessage {...glossary.delete} />
      </Button>
      <DestroyWatchlistDialog
        open={open}
        onClose={() => {
          setOpen(false);
          onClose?.();
        }}
        watchlist={watchlist}
        onSuccess={() => {
          navigate(
            generateSportContextPath(ANY_SPORT_SCOUTING_CENTER_WATCHLISTS)
          );
        }}
      />
    </>
  );
};

DestroyWatchlistButton.fragments = {
  watchlist: gql`
    fragment DestroyWatchlistButton_watchlist on Watchlist {
      slug
      id
      title
      ...DestroyWatchlistDialog_watchlist
    }
    ${DestroyWatchlistDialog.fragments.watchlist}
  ` as TypedDocumentNode<DestroyWatchlistButton_watchlist>,
};
